/**
 * Parsing and URL-building for a post's YouTube video. No network calls -
 * everything here is derived from the video id alone.
 *
 * CommonJS on purpose, same reason as postSlug.js and propertySlug.js:
 * api/render.js requires this from plain Node, and webpack resolves the
 * named imports unchanged for the frontend bundle.
 *
 * The two fields that genuinely need a network call - upload date and
 * duration - are resolved once, server-side, when a post is saved (see
 * api/youtubeMeta.js) and stored on the post itself. Neither the frontend
 * nor api/render.js ever calls out to YouTube in the request path; that
 * would add an external dependency and real latency to every page view of
 * every post with a video, for data that only changes when the video does.
 */

/**
 * Pull an 11-character video id out of any common YouTube URL shape:
 * youtu.be/<id>, youtube.com/watch?v=<id>, /embed/<id>, /shorts/<id> - with
 * or without extra query params (the ?si=... share parameter, &t=…, etc).
 */
function extractYouTubeId(url) {
    const s = String(url || "").trim();
    if (!s) return null;

    const m = s.match(
        /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/
    );
    return m ? m[1] : null;
}

function youtubeWatchUrl(id) {
    return "https://www.youtube.com/watch?v=" + id;
}

/**
 * youtube-nocookie.com defers setting any cookie until the visitor presses
 * play, which is the domain YouTube itself recommends for an embed on a page
 * that does not otherwise need consent-gated tracking.
 */
function youtubeEmbedUrl(id) {
    return "https://www.youtube-nocookie.com/embed/" + id;
}

/**
 * hqdefault.jpg is guaranteed to exist for every public video; the sharper
 * maxresdefault.jpg is not (older or low-resolution uploads lack it, and a
 * missing thumbnail is worse than a slightly softer one).
 */
function youtubeThumbnail(id) {
    return "https://i.ytimg.com/vi/" + id + "/hqdefault.jpg";
}

/**
 * Whole seconds to schema.org's ISO 8601 duration - 751 -> "PT12M31S".
 * Returns null for anything that is not a positive, finite number, so a
 * caller can omit the field rather than emit a broken one.
 */
function secondsToISO8601Duration(totalSeconds) {
    const n = Math.round(Number(totalSeconds));
    if (!Number.isFinite(n) || n <= 0) return null;

    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;

    let out = "PT";
    if (h) out += h + "H";
    if (m) out += m + "M";
    if (s || (!h && !m)) out += s + "S";
    return out;
}

module.exports = {
    extractYouTubeId,
    youtubeWatchUrl,
    youtubeEmbedUrl,
    youtubeThumbnail,
    secondsToISO8601Duration,
};
